import {
  getAccessoriesUsagePdfById,
  getAccessoriesUsageBillPdfById,
} from "api/accessoriesUsageApi"
import { useState } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  Table,
  Row,
  Col,
  Button,
  Badge,
} from "reactstrap"
import { formatReadableDate } from "utils/formatDate"
import { formatBDT } from "utils/formatPrice"
import { calculateBillTotal, calculateSaleSubtotal } from "utils/accessoryUtils"

const AccessoriesUsageViewModal = ({ isOpen, toggle, data }) => {
  const [loading, setLoading] = useState(false)
  const [billLoading, setBillLoading] = useState(false)

  if (!data) return null

  const vehicle = data.vehicle
  const usageBy = data.usage_by
  const moneyReceipt = data.money_receipt
  const customer = data.customer || moneyReceipt?.customer

  const isPackage = data.bill_type === "package"
  const subtotal = isPackage
    ? Number(data.package_price) || 0
    : calculateSaleSubtotal(data.items)
  const discount = Number(data.discount) || 0
  const total = calculateBillTotal(data)

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle} className="border-0 text-center">
        <h4 className="mb-0 fw-bold">ACCESSORIES USAGE DETAILS</h4>
      </ModalHeader>

      <ModalBody className="px-4">
        {/* ===== Meta Info ===== */}
        <Row className="mb-4">
          <Col md={6}>
            <div className="mb-2">
              <strong>Used By:</strong>
              <div>{usageBy?.name || "-"}</div>
            </div>
            {customer && (
              <div className="mb-2">
                <strong>Customer:</strong>
                <div>
                  {customer.name}
                  {customer.mobile ? ` — ${customer.mobile}` : ""}
                </div>
              </div>
            )}
            {vehicle && (
              <>
                <div className="mb-2">
                  <strong>Vehicle:</strong>
                  <div>
                    {vehicle.stock_id} ({vehicle.full_chassis})
                  </div>
                </div>
                <div className="mb-2">
                  <strong>Brand & Model:</strong>
                  <div>
                    {vehicle.brand?.name} {vehicle.model?.name}{" "}
                    {vehicle.year?.name}
                  </div>
                </div>
                <div className="mb-2">
                  <strong>Package & Color:</strong>
                  <div>
                    {vehicle.package?.name} / {vehicle.exterior_color?.name}
                  </div>
                </div>
              </>
            )}
            {moneyReceipt && (
              <div>
                <strong>Money Receipt:</strong>
                <div>
                  {moneyReceipt.mr_no}
                  {moneyReceipt.customer?.name ? ` — ${moneyReceipt.customer.name}` : ""}
                </div>
              </div>
            )}
          </Col>

          <Col md={6} className="text-md-end">
            <div className="mb-2">
              <strong>Usage Date:</strong>
              <div>{formatReadableDate(data.usage_date)}</div>
            </div>
            <div className="mb-2">
              <strong>Bill Type:</strong>
              <div>
                <Badge color={isPackage ? "secondary" : "info"}>
                  {isPackage ? "Package" : "Individual Items"}
                </Badge>
              </div>
            </div>
            <div>
              <strong>Total Accessories:</strong>
              <div>{data.items?.length || 0}</div>
            </div>
          </Col>
        </Row>

        {/* ===== Items Table ===== */}
        <Table bordered responsive className="align-middle">
          <thead className="table-light">
            <tr>
              <th style={{ width: 50 }}>#</th>
              <th>Accessory Name</th>
              <th style={{ width: 90 }} className="text-end">
                Quantity
              </th>
              <th style={{ width: 90 }} className="text-end">
                Unit
              </th>
              <th style={{ width: 120 }} className="text-end">
                Sale Price
              </th>
              <th style={{ width: 130 }} className="text-end">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {data.items?.map((item, index) => (
              <tr key={item.id || index}>
                <td>{index + 1}</td>
                <td>{item.accessory?.name || "-"}</td>
                <td className="text-end">{item.quantity}</td>
                <td className="text-end">
                  {item.accessory?.unit?.name || "-"}
                </td>
                <td className="text-end">{formatBDT(item.sale_price)}</td>
                <td className="text-end">
                  {formatBDT(
                    Number(item.quantity) * Number(item.sale_price || 0)
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {/* ===== Summary ===== */}
        <Row className="justify-content-end">
          <Col md={5}>
            <Table borderless size="sm" className="mb-0">
              <tbody>
                <tr>
                  <td className="fw-semibold">
                    {isPackage ? "Package Price" : "Sub Total"}
                  </td>
                  <td className="text-end">{formatBDT(subtotal)}</td>
                </tr>
                <tr>
                  <td className="fw-semibold">Discount</td>
                  <td className="text-end">- {formatBDT(discount)}</td>
                </tr>
                <tr className="border-top">
                  <td className="fw-bold fs-5">Total</td>
                  <td className="text-end fw-bold fs-5">{formatBDT(total)}</td>
                </tr>
              </tbody>
            </Table>
          </Col>
        </Row>

        <div className="text-end mt-3 d-flex justify-content-end gap-2">
          {data.source === "service_invoice" ? (
            <span className="text-muted align-self-center">
              Consumed via Service Invoice {data.reference_no || ""} — download
              its bill from the Service Invoices page.
            </span>
          ) : (
            <>
              <Button
                color="primary"
                outline
                onClick={() => getAccessoriesUsagePdfById(data?.id, setLoading)}
                disabled={loading}
              >
                {loading ? "Generating..." : "Usage PDF"}
              </Button>
              <Button
                color="success"
                onClick={() =>
                  getAccessoriesUsageBillPdfById(data?.id, setBillLoading)
                }
                disabled={billLoading}
              >
                {billLoading ? "Generating..." : "Download Invoice"}
              </Button>
            </>
          )}
        </div>
      </ModalBody>
    </Modal>
  )
}

export default AccessoriesUsageViewModal
